import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  TEAM_STATS_TEAMS,
  getTeamStatsLabel,
  getStatsCollectionRoot,
} from '../utils/teamStatsConfig';
import GazetteNewspaper from '../components/GazetteNewspaper';
import './GameRecap.css';
import './GladiatorGazette.css';

function endedAtMs(row) {
  const raw = row?.endedAt || row?.details?.date;
  if (!raw) return 0;
  if (typeof raw?.toDate === 'function') return raw.toDate().getTime();
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function scoreLine(row) {
  const us = row?.gameData?.score?.us ?? 0;
  const them = row?.gameData?.score?.them ?? 0;
  const opp = row?.details?.opponent || 'Opponent';
  return `Gladiators ${us} – ${opp} ${them}`;
}

export default function GladiatorGazette() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  const teamFilter = searchParams.get('team') || 'all';
  const selectedId = searchParams.get('id') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const all = [];
        for (const team of TEAM_STATS_TEAMS) {
          const snap = await getDocs(collection(db, getStatsCollectionRoot(), team.teamId, 'gameHistory'));
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            if (!data.recap?.headline) return;
            all.push({
              id: d.id,
              teamId: team.teamId,
              teamLabel: team.label,
              ...data,
            });
          });
        }
        all.sort((a, b) => endedAtMs(b) - endedAtMs(a));
        if (!cancelled) setItems(all);
      } catch (e) {
        console.error('Gazette archive load failed', e);
        if (!cancelled) setError('Could not load Gazettes. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (teamFilter === 'all') return items;
    const team = TEAM_STATS_TEAMS.find((t) => t.label === teamFilter || t.teamId === teamFilter);
    if (!team) return items;
    return items.filter((row) => row.teamId === team.teamId);
  }, [items, teamFilter]);

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return items.find((row) => row.id === selectedId) || null;
  }, [items, selectedId]);

  const setTeamFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('team');
    else next.set('team', value);
    setSearchParams(next);
  };

  const openStory = (row) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', row.id);
    if (teamFilter !== 'all') next.set('team', teamFilter);
    setSearchParams(next);
  };

  const clearStory = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next);
  };

  return (
    <div className="bg-app gazette-archive-page">
      <div className="container py-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h1 className="h3 m-0" style={{ color: 'var(--brand-gold)' }}>
              Gladiator Gazette
            </h1>
            <p className="text-muted-brand mb-0 mt-1">
              Official game reports for Glass City Gladiators families.
            </p>
          </div>
          <Link to="/gladiators-tv" className="btn btn-sm btn-neutral">
            Watch on Gladiators TV
          </Link>
        </div>

        <div className="btn-group mb-4" role="group" aria-label="Filter by team">
          <button
            type="button"
            className={`btn btn-sm ${teamFilter === 'all' ? 'btn-teal' : 'btn-outline-light'}`}
            onClick={() => setTeamFilter('all')}
          >
            All teams
          </button>
          {TEAM_STATS_TEAMS.map((t) => (
            <button
              key={t.teamId}
              type="button"
              className={`btn btn-sm ${teamFilter === t.label ? 'btn-teal' : 'btn-outline-light'}`}
              onClick={() => setTeamFilter(t.label)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="text-muted-brand py-5 text-center">Loading Gazettes…</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && selected && (
          <div className="mb-4">
            <button type="button" className="btn btn-sm btn-neutral mb-3" onClick={clearStory}>
              ← Back to all Gazettes
            </button>
            <GazetteNewspaper
              recap={selected.recap}
              dateStr={selected.details?.date || selected.endedAt || selected.recap?.generatedAt}
              teamLabel={selected.teamLabel || getTeamStatsLabel(selected.teamId)}
            />
            <p className="small text-muted-brand text-center mt-2 mb-0">
              {scoreLine(selected)}
              {selected.details?.location ? ` · ${selected.details.location}` : ''}
            </p>
          </div>
        )}

        {!loading && !error && !selected && (
          <>
            {filtered.length === 0 ? (
              <div className="card card-glad">
                <div className="card-body text-center py-5 text-muted-brand">
                  No Gazettes yet. After a game ends, generate a recap from the Stat Tracker.
                </div>
              </div>
            ) : (
              <div className="row g-3">
                {filtered.map((row) => (
                  <div key={`${row.teamId}-${row.id}`} className="col-12 col-md-6 col-lg-4">
                    <button
                      type="button"
                      className="gazette-archive-card card card-glad h-100 text-start w-100"
                      onClick={() => openStory(row)}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-secondary">{row.teamLabel}</span>
                          <span className="small text-muted-brand">
                            {row.details?.date || '—'}
                          </span>
                        </div>
                        <h2 className="h6 text-light mb-2" style={{ lineHeight: 1.35 }}>
                          {row.recap.headline}
                        </h2>
                        <p className="small text-muted-brand mb-0">{scoreLine(row)}</p>
                        {row.recap.fanQuote?.displayName && (
                          <p className="small mt-2 mb-0" style={{ color: 'var(--brand-gold)' }}>
                            Includes a family quote
                          </p>
                        )}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
